import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rgpdClient } from '../../lib/rgpd';
import { PrivacyPolicyLink } from '../privacy/PrivacyPolicyLink';
import { resolveColor } from '../../utils/colorUtils';
import { logger } from '../../lib/logger';

interface FooterProps {
  config: any;
}

export const Footer: React.FC<FooterProps> = ({ config }) => {
  const { data: rgpdConfig, isLoading, error } = useQuery({
    queryKey: ['rgpd-config'],
    queryFn: () => {
      logger.debug('Loading RGPD config for footer', {
        category: 'UI',
        data: { timestamp: new Date().toISOString() }
      });
      return rgpdClient.getConfig();
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    logger.debug('Footer component mounted', {
      category: 'UI',
      data: { 
        hasConfig: !!config,
        hasRgpdConfig: !!rgpdConfig,
        isLoading,
        error: error ? String(error) : null,
        timestamp: new Date().toISOString()
      }
    });

    return () => {
      logger.debug('Footer component unmounted', {
        category: 'UI',
        data: { timestamp: new Date().toISOString() }
      });
    };
  }, [config, rgpdConfig, isLoading, error]);

  if (!rgpdConfig) {
    logger.debug('Footer not rendering - no RGPD config', {
      category: 'UI',
      data: { 
        isLoading,
        error: error ? String(error) : null
      }
    });
    return null;
  }

  // Résoudre la couleur de fond du footer
  const footerBackgroundColor = resolveColor(config['HFBack-color'], config, config['dark-color']);

  // Résoudre la couleur du texte
  const footerTextColor = resolveColor(config['HFTxt-color'], config, config['light-color']);

  logger.debug('Footer rendering', {
    category: 'UI',
    data: { 
      backgroundColor: footerBackgroundColor,
      textColor: footerTextColor
    }
  });

  return (
    <footer 
      className="fixed bottom-0 left-0 right-0 z-[300] h-10 bg-inherit flex items-center"
      style={{ backgroundColor: footerBackgroundColor }}
    >
      <div className="w-full px-4 flex items-center justify-between">
        <span 
          className="text-xs"
          style={{ color: footerTextColor }}
        >
          © {new Date().getFullYear()} {config.name}
        </span>
        <div className="flex items-center gap-4 text-xs">
          <PrivacyPolicyLink 
            config={{ ...config, 'light-color': footerTextColor }} 
            rgpdConfig={rgpdConfig} 
          />
        </div>
      </div>
    </footer>
  );
};